import { Badge, Button, Spinner, Table } from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import moment from 'moment'
import { IKanbanStage, IKanbanTask } from '../../../types/kanban'

const slash = '/'
const emdash = '\u2014'
const atBadge = '@'

interface Props {
  stages: IKanbanStage[]
  columns: Record<string, IKanbanTask[]>
  loading: boolean
  onEditTask?: (task: IKanbanTask) => void
  onOpenAttachments?: (task: IKanbanTask) => void
  onAddComment?: (task: IKanbanTask) => void
  onArchiveTask?: (task: IKanbanTask) => void
  onDeleteTask?: (task: IKanbanTask) => void
  tasks?: IKanbanTask[]
  currentUserEmail: string
}

export const KanbanListView: React.FC<Props> = ({
  stages,
  columns,
  loading,
  onEditTask,
  onOpenAttachments,
  onAddComment,
  onArchiveTask,
  onDeleteTask,
  tasks,
  currentUserEmail
}) => {
  const intl = useIntl()
  console.log('DEBUG: KanbanListView rendering. Shared View?', !!tasks, 'Count:', tasks?.length)
  if (tasks) {
    console.log('DEBUG: Tasks:', tasks)
  }
  const rows = tasks
    ? tasks.map((task) => ({ stage: task.stage, task }))
    : stages.flatMap((stage) => (columns[stage.id] ?? []).map((task) => ({ stage, task })))

  if (loading) {
    return (
      <div className='kanban-list__loading'>
        <Spinner animation='border' size='sm' /> <span>{intl.formatMessage({id: 'common.loading', defaultMessage: 'Loading...'})}</span>
      </div>
    )
  }

  if (!rows.length) {
    return <div className='kanban-list__empty'>{intl.formatMessage({id: 'leads.kanban.noTasksBoard', defaultMessage: 'There is no task in the board'})}</div>
  }

  const renderChecklist = (task: IKanbanTask) => {
    if (!task.checklistItems?.length) return emdash
    const completed = task.checklistItems.filter((item) => item.isCompleted).length
    return (
      <span className='kanban-list__checklist'>
        <i className='bi bi-check-square me-1' /> {completed}{slash}{task.checklistItems.length}
      </span>
    )
  }

  const renderPriorityBadge = (task: IKanbanTask) => {
    if (!task.priority || task.priority === 'NORMAL') {
      return <Badge bg='light' text='dark'></Badge>
    }
    if (task.priority === 'URGENT') {
      return (
        <span className='kanban-card__priority kanban-card__priority--urgent'>
          <i className='bi bi-exclamation-octagon-fill me-1' />
          <FormattedMessage id='leads.kanban.priorityUrgent' defaultMessage='Urgent' />
        </span>
      )
    }
    if (task.priority === 'LOW') {
      return (
        <span className='kanban-card__priority kanban-card__priority--low'>
          <i className='bi bi-info-circle-fill me-1' />
          <FormattedMessage id='leads.kanban.priorityLow' defaultMessage='Low' />
        </span>
      )
    }
    return (
      <span className='kanban-card__priority kanban-card__priority--high'>
        <i className='bi bi-exclamation-triangle-fill me-1' />
        <FormattedMessage id='leads.kanban.priorityHigh' defaultMessage='High' />
      </span>
    )
  }

  return (
    <div className='kanban-list table-scrollable table-container'>
      <Table
        hover
        size='sm'
        className='table-scrollable table-scroll  table table-striped table-hover  '
      >
        <thead>
          <tr>
            <th className='fw-bold'>{intl.formatMessage({id: 'leads.kanban.thColumnStage', defaultMessage: 'Column/Stage'})}</th>
            <th className='fw-bold'>{intl.formatMessage({id: 'leads.kanban.thTask', defaultMessage: 'Task'})}</th>
            <th className='fw-bold'>{intl.formatMessage({id: 'leads.kanban.thAssignment', defaultMessage: 'Assignment'})}</th>
            <th className='fw-bold'>{intl.formatMessage({id: 'leads.kanban.thDueDate', defaultMessage: 'Due date'})}</th>
            <th className='fw-bold'>{intl.formatMessage({id: 'leads.kanban.thPriority', defaultMessage: 'Priority'})}</th>
            <th className='fw-bold'>{intl.formatMessage({id: 'leads.kanban.thChecklist', defaultMessage: 'Checklist'})}</th>
            <th className='fw-bold'><FormattedMessage id='common.field.actions' defaultMessage='Actions' /></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ stage, task }) => (
            <tr key={task.id}>
              <td className='fw-semibold'>{stage.name}</td>
              <td>
                <div className='fw-semibold'>
                  {task.title}
                  {(() => {
                    const isAssignee = task.assignee?.email === currentUserEmail
                    const isParticipant = task.participants?.some(
                      (p) => p.user?.email === currentUserEmail
                    )
                    if (!isAssignee && !isParticipant) {
                      return (
                        <Badge
                          bg='info'
                          className='ms-2'
                          title={intl.formatMessage({id: 'leads.kanban.mentionedTooltip', defaultMessage: 'You are mentioned in this task'})}
                        >
                          {atBadge}
                        </Badge>
                      )
                    }
                    return null
                  })()}
                </div>
                {task.labelText && <div className='text-muted small'>{task.labelText}</div>}
              </td>
              <td>
                {task.assignee
                  ? [task.assignee.firstName, task.assignee.lastName].join(' ')
                  : intl.formatMessage({id: 'leads.kanban.unassigned', defaultMessage: 'Unassigned'})}
              </td>
              <td>{task.dueDate ? moment(task.dueDate).format('MMM DD') : emdash}</td>
              <td>{renderPriorityBadge(task)}</td>
              <td>{renderChecklist(task)}</td>
              <td className='kanban-list__actions'>
                {onEditTask && (
                  <Button size='sm' variant='link' onClick={() => onEditTask(task)}>
                    <i className='bi bi-pencil-square' />
                  </Button>
                )}
                {onOpenAttachments && (
                  <Button size='sm' variant='link' onClick={() => onOpenAttachments(task)}>
                    <i className='bi bi-paperclip' />
                  </Button>
                )}
                {onAddComment && (
                  <Button size='sm' variant='link' onClick={() => onAddComment(task)}>
                    <i className='bi bi-chat-dots' />
                  </Button>
                )}
                {onArchiveTask && (
                  <Button size='sm' variant='link' onClick={() => onArchiveTask(task)}>
                    <i className='bi bi-archive' />
                  </Button>
                )}
                {onDeleteTask && (
                  <Button
                    size='sm'
                    variant='link'
                    className='text-danger'
                    onClick={() => onDeleteTask(task)}
                  >
                    <i className='bi bi-trash' />
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}
